import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import "bootstrap/dist/css/bootstrap.min.css";
import CommonService from "../../services/common.service";
import { API_URLS } from "../../constants/api-urls";
import "../../assets/styles/logistics_compare_quotes.scss";

function toNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function isoToShort(v) {
  if (!v) return "—";
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    return d.toISOString().slice(0, 10);
  } catch {
    return String(v);
  }
}

function ratingFromScore(score) {
  // Map score (60..98) -> rating (4.0..4.9)
  const r = 4 + (score - 60) * (0.9 / 38);
  return Math.round(r * 10) / 10;
}

function buildIncExc(quote) {
  const inc = [];
  const exc = [];
  if (!quote || typeof quote !== "object") return { inc, exc };

  const addInc = (label, ok) => {
    if (ok) inc.push(label);
  };

  addInc("Ocean freight", toNum(quote.oceanFreight) != null);
  addInc("Port handling", toNum(quote.portHandling) != null);
  addInc("Documentation", toNum(quote.documentation) != null);
  addInc("Container deposit", toNum(quote.containerDeposit) != null);
  if (quote.loadingIncluded) addInc("Loading charges", true);
  if (quote.unloadingIncluded) addInc("Unloading charges", true);

  // Exclusions: use exclusions string split
  const exStr = String(quote.exclusions || "").trim();
  if (exStr) {
    exStr
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .slice(0, 5)
      .forEach((x) => exc.push(x));
  }

  return { inc, exc };
}

export default function LogisticsCompareQuotesPage() {
  const { submissionId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [rfqRef, setRfqRef] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [pendingQuoteApprovalCount, setPendingQuoteApprovalCount] = useState(0);
  const [isP2pLinked, setIsP2pLinked] = useState(false);
  const [view, setView] = useState("card"); // card | table
  const [acceptingId, setAcceptingId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectModal, setRejectModal] = useState({ open: false, quoteId: null, reason: "", comment: "" });
  const [expandedInc, setExpandedInc] = useState(() => new Set());
  const [expandedExc, setExpandedExc] = useState(() => new Set());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const idNum = submissionId != null ? parseInt(String(submissionId), 10) : NaN;
        if (!Number.isFinite(idNum) || idNum <= 0) throw new Error("Invalid submission id");
        const res = await CommonService.get(API_URLS.LOGISTICS_PROVIDER.RFQ_MY_SUBMISSION_QUOTES(idNum));
        const body = res?.data ?? res ?? {};
        const data = body?.data ?? body;
        const items = Array.isArray(data?.items) ? data.items : [];
        if (!cancelled) {
          setRfqRef(data?.rfqReferenceNumber || null);
          setQuotes(items);
          setPendingQuoteApprovalCount(Number(data?.pendingQuoteApprovalCount ?? 0));
          setIsP2pLinked(Boolean(data?.isP2pLinked));
        }
      } catch (e) {
        toast.error(e?.response?.data?.message || e?.message || "Could not load quotes.");
        if (!cancelled) setQuotes([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [submissionId]);

  const computed = useMemo(() => {
    const baseRows = quotes.map((x) => {
      const quote = x.quote || {};
      const total = x.totalAmount != null ? Number(x.totalAmount) : toNum(quote.totalAmount);
      const transit = toNum(quote.transitDays);
      const { inc, exc } = buildIncExc(quote);
      return {
        ...x,
        total,
        transit,
        inc,
        exc,
        validUntil: isoToShort(quote.quoteExpiryDate),
        paymentTerms: String(quote.paymentTerms || "—"),
        accepted: String(x.status || "").toLowerCase() === "accepted",
      };
    });

    const totals = baseRows.map((r) => r.total).filter((n) => Number.isFinite(n));
    const bestPrice = totals.length ? Math.min(...totals) : null;
    const maxPrice = totals.length ? Math.max(...totals) : null;
    const saveUpTo = bestPrice != null && maxPrice != null ? Math.max(0, maxPrice - bestPrice) : null;

    const transitVals = baseRows.map((r) => r.transit).filter((n) => Number.isFinite(n));
    const minTransit = transitVals.length ? Math.min(...transitVals) : null;
    const maxTransit = transitVals.length ? Math.max(...transitVals) : null;

    // Build "UI-like" score relative to other quotes (higher is better)
    const rows = baseRows.map((r) => {
      const hasPrice = bestPrice != null && maxPrice != null && Number.isFinite(r.total);
      const hasTransit = minTransit != null && maxTransit != null && Number.isFinite(r.transit);

      const priceNorm =
        hasPrice && maxPrice !== bestPrice ? 1 - (Number(r.total) - bestPrice) / (maxPrice - bestPrice) : hasPrice ? 1 : 0.5;
      const transitNorm =
        hasTransit && maxTransit !== minTransit
          ? 1 - (Number(r.transit) - minTransit) / (maxTransit - minTransit)
          : hasTransit
            ? 1
            : 0.5;

      const score = Math.max(60, Math.min(98, Math.round(70 + priceNorm * 20 + transitNorm * 8)));
      const rating = ratingFromScore(score);
      return { ...r, score, rating };
    });

    const best = bestPrice != null ? rows.find((r) => r.total === bestPrice) : null;
    const fastest = rows
      .filter((r) => r.transit != null)
      .slice()
      .sort((a, b) => (a.transit ?? 999) - (b.transit ?? 999))[0];
    const highest = rows.slice().sort((a, b) => b.rating - a.rating)[0];

    return { rows, bestPrice, saveUpTo, best, fastest, highest };
  }, [quotes]);

  const acceptQuote = async (quoteId) => {
    try {
      const idNum = submissionId != null ? parseInt(String(submissionId), 10) : NaN;
      if (!Number.isFinite(idNum) || idNum <= 0) throw new Error("Invalid submission id");
      setAcceptingId(quoteId);
      await CommonService.post(API_URLS.LOGISTICS_PROVIDER.RFQ_MY_SUBMISSION_ACCEPT_QUOTE(idNum, quoteId), {});
      toast.success("Quote accepted.");
      // refresh list so status reflects
      const res = await CommonService.get(API_URLS.LOGISTICS_PROVIDER.RFQ_MY_SUBMISSION_QUOTES(idNum));
      const body = res?.data ?? res ?? {};
      const data = body?.data ?? body;
      setQuotes(Array.isArray(data?.items) ? data.items : []);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Could not accept quote.");
    } finally {
      setAcceptingId(null);
    }
  };

  const rejectQuote = async (quoteId, reason, comment) => {
    try {
      const idNum = submissionId != null ? parseInt(String(submissionId), 10) : NaN;
      if (!Number.isFinite(idNum) || idNum <= 0) throw new Error("Invalid submission id");
      setRejectingId(quoteId);
      await CommonService.post(API_URLS.LOGISTICS_PROVIDER.RFQ_MY_SUBMISSION_REJECT_QUOTE(idNum, quoteId), {
        reason,
        comment,
      });
      toast.success("Quote rejected.");
      const res = await CommonService.get(API_URLS.LOGISTICS_PROVIDER.RFQ_MY_SUBMISSION_QUOTES(idNum));
      const body = res?.data ?? res ?? {};
      const data = body?.data ?? body;
      setQuotes(Array.isArray(data?.items) ? data.items : []);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Could not reject quote.");
    } finally {
      setRejectingId(null);
    }
  };

  const REJECTION_REASONS = [
    "Price too high",
    "Transit time not acceptable",
    "Validity too short",
    "Missing inclusions / unclear charges",
    "Payment terms not acceptable",
    "Other",
  ];

  if (loading) {
    return (
      <div className="container-fluid py-4">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="lcq-page">
      <div className="container-fluid py-4">
        <div className="lcq-topbar">
          <div>
            <div className="d-flex align-items-center gap-2 mb-1">
              <button type="button" className="btn btn-link p-0 fw-bold text-decoration-none" onClick={() => navigate(-1)}>
                ←
              </button>
              <h2 className="mb-0">Compare Quotes</h2>
            </div>
            <div className="lcq-sub">RFQ #{rfqRef || "—"} · {computed.rows.length} quotes received</div>
          </div>

          <div className="lcq-toggle">
            <button
              type="button"
              className={`lcq-pill ${view === "card" ? "is-active" : ""}`}
              onClick={() => setView("card")}
            >
              Card View
            </button>
            <button
              type="button"
              className={`lcq-pill ${view === "table" ? "is-active" : ""}`}
              onClick={() => setView("table")}
            >
              Comparison Table
            </button>
          </div>
        </div>

        {!loading && computed.rows.length === 0 && isP2pLinked && pendingQuoteApprovalCount > 0 ? (
          <div className="alert alert-warning mt-3 mb-0" role="status">
            <strong>Pending Logistics Approval</strong>
            <div className="small mt-1 mb-0">
              {pendingQuoteApprovalCount} provider quote
              {pendingQuoteApprovalCount === 1 ? "" : "s"} submitted and awaiting internal P2P approval.
              Compare Quotes will appear here once approvers complete the workflow.
            </div>
          </div>
        ) : null}

        <div className="lcq-metrics">
          <div className="lcq-metric lcq-metric--green">
            <div>
              <div className="k">Best Price</div>
              <div className="v">
                {computed.bestPrice != null ? `USD ${computed.bestPrice.toLocaleString()}` : "—"}
              </div>
              <div className="s">
                {computed.best?.providerCompanyName
                  ? computed.best.providerCompanyName
                  : computed.saveUpTo != null
                    ? `Save up to $${computed.saveUpTo.toLocaleString()}`
                    : ""}
              </div>
            </div>
            <div style={{ fontSize: 22, fontWeight: 900, color: "#16a34a" }}>↘</div>
          </div>

          <div className="lcq-metric lcq-metric--blue">
            <div>
              <div className="k">Fastest Delivery</div>
              <div className="v">{computed.fastest?.transit != null ? `${computed.fastest.transit} days` : "—"}</div>
              <div className="s">{computed.fastest?.providerCompanyName || ""}</div>
            </div>
            <div style={{ fontSize: 22, fontWeight: 900, color: "#2563eb" }}>⚡</div>
          </div>

          <div className="lcq-metric lcq-metric--yellow">
            <div>
              <div className="k">Highest Rated</div>
              <div className="v">
                {computed.highest ? `${computed.highest.rating} ★` : "—"}
              </div>
              <div className="s">Top provider rating</div>
            </div>
            <div style={{ fontSize: 22, fontWeight: 900, color: "#f59e0b" }}>🏅</div>
          </div>
        </div>

        {view === "card" ? (
          <div className="lcq-grid">
            {computed.rows.map((q) => {
              const isBest = computed.bestPrice != null && q.total === computed.bestPrice;
              const incExpanded = expandedInc.has(q.id);
              const excExpanded = expandedExc.has(q.id);
              return (
                <div className="lcq-card" key={q.id}>
                  <div className="lcq-card-head">
                    <div className="lcq-provider">
                      <div className="lcq-avatar" />
                      <div style={{ minWidth: 0 }}>
                        <div className="name">{q.providerCompanyName}</div>
                        <div className="meta">
                          {Number(q.rating || 0).toFixed(1)} ★ &nbsp; <span className="text-muted">Score:</span> {q.score}
                        </div>
                      </div>
                    </div>
                    {isBest ? <span className="lcq-badge">Best Price</span> : null}
                  </div>

                  <div className="lcq-amount">
                    <div className="k">Total Quote</div>
                    <div className="v">USD {Number.isFinite(q.total) ? q.total.toLocaleString() : "—"}</div>
                  </div>

                  <div className="lcq-mini-row">
                    <div className="lcq-mini">
                      <div className="k">Transit Time</div>
                      <div className="v">{q.transit != null ? `${q.transit} days` : "—"}</div>
                    </div>
                    <div className="lcq-mini lcq-mini--pink">
                      <div className="k">Valid Until</div>
                      <div className="v">{q.validUntil}</div>
                    </div>
                  </div>

                  <div className="lcq-list">
                    <div className="title">
                      <span style={{ color: "#16a34a" }}>✓</span> Inclusions ({q.inc.length})
                    </div>
                    <ul>
                      {(incExpanded ? q.inc : q.inc.slice(0, 3)).map((x) => (
                        <li key={x}>{x}</li>
                      ))}
                    </ul>
                    {q.inc.length > 3 ? (
                      <button
                        type="button"
                        className="lcq-more btn btn-link p-0"
                        onClick={() =>
                          setExpandedInc((prev) => {
                            const next = new Set(prev);
                            if (next.has(q.id)) next.delete(q.id);
                            else next.add(q.id);
                            return next;
                          })
                        }
                      >
                        {incExpanded ? "Show less" : `+${q.inc.length - 3} more`}
                      </button>
                    ) : null}
                  </div>

                  <div className="lcq-list">
                    <div className="title">
                      <span style={{ color: "#ef4444" }}>✕</span> Exclusions ({q.exc.length})
                    </div>
                    <ul>
                      {(excExpanded ? q.exc : q.exc.slice(0, 2)).map((x) => (
                        <li key={x}>{x}</li>
                      ))}
                    </ul>
                    {q.exc.length > 2 ? (
                      <button
                        type="button"
                        className="lcq-more btn btn-link p-0"
                        onClick={() =>
                          setExpandedExc((prev) => {
                            const next = new Set(prev);
                            if (next.has(q.id)) next.delete(q.id);
                            else next.add(q.id);
                            return next;
                          })
                        }
                      >
                        {excExpanded ? "Show less" : `+${q.exc.length - 2} more`}
                      </button>
                    ) : null}
                  </div>

                  <div className="lcq-actions">
                    <button
                      type="button"
                      className="btn btn-success flex-grow-1 lcq-btn-accept"
                      onClick={() => acceptQuote(q.id)}
                      disabled={acceptingId === q.id || rejectingId === q.id || q.accepted || q.status === "rejected"}
                    >
                      <i className="far fa-thumbs-up me-2" aria-hidden />
                      {q.accepted ? "Accepted" : "Accept"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger flex-grow-1 lcq-btn-action"
                      onClick={() => setRejectModal({ open: true, quoteId: q.id, reason: "", comment: "" })}
                      disabled={acceptingId === q.id || rejectingId === q.id || q.accepted || q.status === "rejected"}
                      title="Reject"
                    >
                      <i className="fas fa-times me-2" aria-hidden />
                      Reject
                    </button>
                    <Link
                      to={`/logistics-rfqs-sent/${submissionId}/quotes/${q.id}`}
                      className="btn btn-outline-secondary flex-grow-1 lcq-btn-action"
                      title="Quote details"
                    >
                      <i className="far fa-file-alt me-2" aria-hidden />
                      Quote details
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="lcq-table-wrap">
            <div className="p-3 border-bottom">
              <div className="fw-bold">Detailed Comparison</div>
              <div className="text-muted small">Side-by-side comparison of all quotes</div>
            </div>
            <table className="lcq-table">
              <thead>
                <tr>
                  <th style={{ width: 220 }}>Criteria</th>
                  {computed.rows.map((q) => (
                    <th key={q.id}>
                      <div className="d-flex align-items-center gap-2">
                        <span className="lcq-avatar" style={{ width: 22, height: 22 }} />
                        <div>
                          <div style={{ fontWeight: 1000 }}>{q.providerCompanyName}</div>
                          <div className="text-muted small">{q.rating} ★</div>
                        </div>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr style={{ background: "#fff7ed" }}>
                  <td className="lcq-criteria">Total Amount</td>
                  {computed.rows.map((q) => {
                    const isLow = computed.bestPrice != null && q.total === computed.bestPrice;
                    return (
                      <td key={q.id} style={{ fontWeight: 1000, color: "#ea580c" }}>
                        USD {Number.isFinite(q.total) ? q.total.toLocaleString() : "—"}
                        {isLow ? <span className="lcq-tag lcq-tag--low">Lowest</span> : null}
                      </td>
                    );
                  })}
                </tr>
                <tr>
                  <td className="lcq-criteria">Transit Time</td>
                  {computed.rows.map((q) => {
                    const isFast =
                      computed.fastest?.id != null && Number(computed.fastest.id) === Number(q.id);
                    return (
                      <td key={q.id}>
                        <span className="text-muted me-2">🕒</span>
                        {q.transit != null ? `${q.transit} days` : "—"}
                        {isFast ? <span className="lcq-tag lcq-tag--fast">Fastest</span> : null}
                      </td>
                    );
                  })}
                </tr>
                <tr>
                  <td className="lcq-criteria">Valid Until</td>
                  {computed.rows.map((q) => (
                    <td key={q.id}>
                      <span className="text-muted me-2">📅</span>
                      {q.validUntil}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="lcq-criteria">Payment Terms</td>
                  {computed.rows.map((q) => (
                    <td key={q.id}>{q.paymentTerms}</td>
                  ))}
                </tr>
                <tr>
                  <td className="lcq-criteria">Overall Score</td>
                  {computed.rows.map((q) => (
                    <td key={q.id}>
                      <span className={`lcq-score ${q.score >= 92 ? "lcq-score--g" : "lcq-score--b"}`}>
                        {q.score}/100
                      </span>
                    </td>
                  ))}
                </tr>
                <tr>
                  <td className="lcq-criteria">Action</td>
                  {computed.rows.map((q) => (
                    <td key={q.id}>
                      <div className="d-flex gap-2 align-items-center" style={{ maxWidth: 320 }}>
                        <button
                          type="button"
                          className="btn btn-success flex-grow-1 lcq-btn-accept lcq-btn-accept--table"
                          onClick={() => acceptQuote(q.id)}
                          disabled={acceptingId === q.id || rejectingId === q.id || q.accepted || q.status === "rejected"}
                        >
                          <i className="far fa-thumbs-up me-2" aria-hidden />
                          {q.accepted ? "Accepted" : "Accept Quote"}
                        </button>
                        <button
                          type="button"
                          className="btn btn-danger flex-grow-1 lcq-btn-action"
                          onClick={() => setRejectModal({ open: true, quoteId: q.id, reason: "", comment: "" })}
                          disabled={acceptingId === q.id || rejectingId === q.id || q.accepted || q.status === "rejected"}
                          title="Reject"
                        >
                          <i className="fas fa-times me-2" aria-hidden />
                          Reject
                        </button>
                        <Link
                          to={`/logistics-rfqs-sent/${submissionId}/quotes/${q.id}`}
                          className="btn btn-outline-secondary flex-grow-1 lcq-btn-action"
                          title="Quote details"
                        >
                          <i className="far fa-file-alt me-2" aria-hidden />
                          Quote details
                        </Link>
                      </div>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-3">
          <Link to="/logistics-rfqs-sent" className="btn btn-outline-secondary">
            Back to Sent RFQs
          </Link>
        </div>
      </div>

      {/* Reject Quote Modal */}
      {rejectModal.open ? (
        <>
          <div
            className="modal-backdrop show"
            onClick={() => setRejectModal({ open: false, quoteId: null, reason: "", comment: "" })}
          />
          <div className="modal show d-block" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Reject Quote</h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setRejectModal({ open: false, quoteId: null, reason: "", comment: "" })}
                  />
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label fw-bold">Reason</label>
                    <select
                      className="form-select"
                      value={rejectModal.reason}
                      onChange={(e) => setRejectModal((p) => ({ ...p, reason: e.target.value }))}
                    >
                      <option value="">Select reason</option>
                      {REJECTION_REASONS.map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-2">
                    <label className="form-label fw-bold">Comment</label>
                    <textarea
                      className="form-control"
                      rows={3}
                      placeholder="Enter rejection comment"
                      value={rejectModal.comment}
                      onChange={(e) => setRejectModal((p) => ({ ...p, comment: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setRejectModal({ open: false, quoteId: null, reason: "", comment: "" })}
                    disabled={rejectingId === rejectModal.quoteId}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    disabled={
                      rejectingId === rejectModal.quoteId ||
                      !String(rejectModal.reason || "").trim() ||
                      !String(rejectModal.comment || "").trim()
                    }
                    onClick={async () => {
                      const reason = String(rejectModal.reason || "").trim();
                      const comment = String(rejectModal.comment || "").trim();
                      if (!reason) {
                        toast.error("Please select a reason.");
                        return;
                      }
                      if (!comment) {
                        toast.error("Please enter a comment.");
                        return;
                      }
                      await rejectQuote(rejectModal.quoteId, reason, comment);
                      setRejectModal({ open: false, quoteId: null, reason: "", comment: "" });
                    }}
                  >
                    Reject Quote
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

